import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { fromZonedTime } from 'date-fns-tz/fromZonedTime';

import { timezoneOptions } from '@/generated/backend/timezoneOptions';
import { serializeDatetime } from '@/lib/dateFormatter';
import { TimezoneFormat } from '@/types/enums';

import type { TaskUtils, TaskWorkspaceFields } from './types';
import { warnSdkUtilFallback } from './warn';

const validTimezones = new Set(timezoneOptions.map((option) => option.value));
const LOCAL_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_DATETIME_REGEX = /^(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{2}):(\d{2})(\.\d{3}|\.\d{6})?$/;

type InterpretBoundary = 'exact' | 'start_of_day' | 'end_of_day';

function utcOffset(timezone: string): string {
  return `UTC${formatInTimeZone(new Date(), timezone, 'xxx')}`;
}

function abbreviation(timezone: string): string {
  return formatInTimeZone(new Date(), timezone, 'zzz');
}

function city(timezone: string): string {
  const parts = timezone.split('/');
  return (parts[parts.length - 1] ?? '').replace(/_/g, ' ');
}

function fullLabel(timezone: string): string {
  const display = timezone.replace(/\//g, ' / ').replace(/_/g, ' ');
  return `${display} (${utcOffset(timezone)})`;
}

export function formatTimezone(timezone: unknown, format: TimezoneFormat = 'full'): string | null {
  if (typeof timezone !== 'string' || !validTimezones.has(timezone)) {
    warnSdkUtilFallback('timezone.format', 'The timezone is invalid; returning null.');
    return null;
  }

  switch (format) {
    case TimezoneFormat.Full:
      return fullLabel(timezone);
    case TimezoneFormat.Offset:
      return utcOffset(timezone);
    case TimezoneFormat.Abbreviation:
      return abbreviation(timezone);
    case TimezoneFormat.City:
      return city(timezone);
    default:
      warnSdkUtilFallback('timezone.format', 'The timezone format is invalid; returning null.');
      return null;
  }
}

function extractDatePart(value: string): string | null {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * Reduce a local datetime to the shape date-fns can parse, accepting a space
 * separator and a single-digit hour alongside ISO-8601.
 */
function canonicalizeLocalDatetime(value: string): string {
  const match = value.match(LOCAL_DATETIME_REGEX);

  if (!match) {
    return value;
  }

  const [, date, hour, minute, second, fraction] = match;
  return `${date}T${hour.padStart(2, '0')}:${minute}:${second}${fraction ?? ''}`;
}

function normalizeLocalValue(value: string, boundary: InterpretBoundary): string {
  if (boundary === 'exact') {
    return LOCAL_DATE_REGEX.test(value)
      ? `${value}T00:00:00.000`
      : canonicalizeLocalDatetime(value);
  }

  const datePart = extractDatePart(value);

  if (!datePart) {
    throw new Error(`Invalid local date or datetime: ${value}`);
  }

  return boundary === 'start_of_day' ? `${datePart}T00:00:00.000` : `${datePart}T23:59:59.999`;
}

export function interpretTimezone(
  value: unknown,
  timezone: string | null | undefined,
  boundary: InterpretBoundary = 'exact'
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value !== 'string') {
    throw new Error(`Invalid local date or datetime: ${String(value)}`);
  }

  if (timezone === null || timezone === undefined || timezone === '') {
    warnSdkUtilFallback('timezone.interpret', 'No timezone was provided; returning null.');
    return null;
  }

  if (!validTimezones.has(timezone)) {
    throw new Error(`Invalid timezone: ${timezone}`);
  }

  const localValue = normalizeLocalValue(value, boundary);
  const utcDate = fromZonedTime(localValue, timezone);

  if (Number.isNaN(utcDate.getTime())) {
    throw new Error(`Invalid local date or datetime: ${value}`);
  }

  return serializeDatetime(utcDate) as string;
}

export function workspaceTimezone(workspaceFields: TaskWorkspaceFields): string {
  const value = workspaceFields['default_timezone'];
  return typeof value === 'string' && value.trim() !== '' ? value : 'America/Chicago';
}

export function createTimezoneUtils(workspaceFields: TaskWorkspaceFields): TaskUtils['timezone'] {
  return {
    format: (timezone, format) => formatTimezone(timezone, format),
    interpret: (value, timezone) => interpretTimezone(value, timezone, 'exact'),
    get: () => workspaceTimezone(workspaceFields)
  };
}
